// The places lab (`?scene=places`, gate C4 "Water and interiors", and the building kit the world places): a small town
// square (cottages, the smithy, the shop, the elder's house, a well, stalls, lanterns), a ruins corner with the
// chapel whose door is the transition test, a stream with the footbridge, a pond, and the crypt away from the town;
// the Wanderer on the valley's machinery and its camera. Built from the content (src/places/content.ts) through
// @rpg/meshkit's kit, @rpg/collision's water shapes, @rpg/world's interiors and @rpg/look's kit and water materials.
// `__rpg.places` is the handle: the interiors and doors, water queries, the pieces, the kit's hash, the counts and a
// perf tour. Switches: `?night` (the lab's night stand-in: lit windows and lamps).
import * as THREE from "three";
import { hashGeometry, type KitTier } from "@rpg/meshkit";
import { overlay, perfReport, runTour, type TourSegment } from "@rpg/perf";
import type { LowPolyLook } from "@rpg/look";
import type { Figure, Motion } from "@rpg/figure";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { ValleyCamera } from "../valley/camera";
import type { ValleyVariant } from "../valley/scene";
import { placesContent, type PlacesContent } from "./content";
import { PLACES_FAULTS, placesFaultGroup } from "./faults";
import { buildPlacesWorld, PLACES_SPAWN, type PlacesWorld } from "./layout";
import { placesVariant, type PlacesSim } from "./module";
import { mountPlacesView, type PlacesView } from "./view";

export { placesFaultGroup, PLACES_FAULT_NAMES } from "./faults";
export { waterAt } from "./water";

/** Whether the page runs the places lab. */
export const isPlacesScene = (search: string): boolean => new URLSearchParams(search).get("scene") === "places";

/** The places lab, before and after its world exists. */
export interface PlacesMount {
  variant: ValleyVariant;
  content: PlacesContent;
  world: PlacesWorld;
  /** Mount the view (in the valley look's warm-up). */
  mountView(o: { scene: THREE.Scene; look: LowPolyLook; hero: { figure: Figure; motion: Motion }; camera: ValleyCamera | null; renderer: THREE.WebGLRenderer }): void;
  /** Keys for `__rpg.places`. */
  handle(): ReturnType<typeof placesHandle>;
  /** A summary for `state()`. */
  state(): Record<string, unknown>;
}

/** Build the places lab: its content, world and variant (the view mounts later). */
export function placesMount(o: { content: SpikeContent; runtime: Runtime; tier: string; search: string }): PlacesMount {
  // the planted defects first: the kit reads its own while it builds
  placesFaultGroup(o.search);
  const content = placesContent(o.content);
  const tier: KitTier = o.tier === "low" ? "low" : "high";
  const world = buildPlacesWorld(content, tier);
  let sim: PlacesSim | null = null;
  let view: PlacesView | null = null;
  let renderer: THREE.WebGLRenderer | null = null;
  let scene: THREE.Scene | null = null;
  let look: LowPolyLook | null = null;
  const variant = placesVariant({ world, content, ready: (s) => void (sim = s) });
  const night = new URLSearchParams(o.search).has("night");
  const self: PlacesMount = {
    variant,
    content,
    world,
    mountView(v) {
      renderer = v.renderer;
      scene = v.scene;
      look = v.look;
      view = mountPlacesView({ runtime: o.runtime, scene: v.scene, look: v.look, content, world, sim: sim!, camera: v.camera, renderer: v.renderer, night });
    },
    handle: () => placesHandle({ runtime: o.runtime, world, sim: () => sim!, view: () => view, renderer: () => renderer, scene: () => scene, look: () => look, tier }),
    state: () => {
      const s = sim?.state();
      return { inside: s?.interiors.inside ?? null, transit: s?.interiors.transit ?? null, ...(view ? { view: view.state() } : {}) };
    },
  };
  return self;
}

/** The yaw from a point to another (the controller's and camera's convention: forward is (sin yaw, cos yaw)). */
const yawTo = (dx: number, dz: number): number => Math.atan2(dx, dz);

function placesHandle(d: {
  runtime: Runtime;
  world: PlacesWorld;
  sim: () => PlacesSim;
  view: () => PlacesView | null;
  renderer: () => THREE.WebGLRenderer | null;
  scene: () => THREE.Scene | null;
  look: () => LowPolyLook | null;
  tier: KitTier;
}) {
  const w = d.world;
  const rig = () => d.sim().rig();
  /** Warp the hero `dist` m outside a room's door, facing it, with the sim's camera yaw held facing it too. */
  const approach = (id: string, dist = 3): { yaw: number; door: { x: number; z: number } } => {
    const room = w.rooms.get(id) ?? w.placed.find((p) => p.id === id);
    if (!room?.piece.interior) throw new Error(`places: no room "${id}"`);
    const i = room.piece.interior;
    const c = Math.cos(room.place.yaw);
    const s = Math.sin(room.place.yaw);
    const dx = room.place.x + i.door.x * c + i.door.z * s;
    const dz = room.place.z - i.door.x * s + i.door.z * c;
    const nx = i.door.nx * c + i.door.nz * s;
    const nz = -i.door.nx * s + i.door.nz * c;
    const x = dx + nx * dist;
    const z = dz + nz * dist;
    const yaw = yawTo(-nx, -nz);
    const r = rig();
    r.warpHero(x, r.terrainAt(x, z), z, yaw);
    r.yawHeld = true;
    r.camera?.setYaw(yaw);
    r.control().cameraYaw = yaw;
    return { yaw, door: { x: dx, z: dz } };
  };
  /** Gate C4's tour: the square, into the smithy, the ruins, the bridge, the pond, the crypt (low and high tiers). */
  const tour = (ffMs = 100): void => {
    const renderer = d.renderer();
    const scene = d.scene();
    const look = d.look();
    if (!renderer || !scene || !look) throw new Error("places tour: no view");
    const ui = overlay(d.tier);
    document.getElementById("perf-tour")?.setAttribute("data-no-strings", "");
    const t0 = { s: 0 };
    const at = (x: number, z: number, yaw: number) => () => {
      const r = rig();
      r.warpHero(x, r.terrainAt(x, z), z, yaw);
      r.yawHeld = true;
      r.camera?.setYaw(yaw);
      r.control().cameraYaw = yaw;
    };
    const stick = (x: number, y: number) => rig().actions.setStick("move", { x, y });
    const hold = (secs: number) => (t: number) => t - t0.s > secs;
    const segs: TourSegment[] = [
      { name: "warmup", start: at(PLACES_SPAWN.x, PLACES_SPAWN.z, PLACES_SPAWN.yaw), tick: hold(0.8), max: 3 },
      { name: "square", start: at(0, 9, Math.PI), tick: hold(1.5), max: 4 },
      {
        name: "smithy",
        start: () => {
          approach("smithy", 3);
          stick(0, 1);
        },
        tick: (t) => {
          if (t - t0.s > 2.4) stick(0, 0);
          return t - t0.s > 3.4;
        },
        max: 6,
      },
      { name: "ruins", start: at(22, -14, Math.PI * 0.8), tick: hold(1.5), max: 4 },
      {
        name: "bridge",
        start: () => {
          at(0, 14, 0)();
          stick(0, 1);
        },
        tick: (t) => {
          if (t - t0.s > 3) stick(0, 0);
          return t - t0.s > 3.5;
        },
        max: 6,
      },
      { name: "pond", start: at(18, 28, Math.PI / 2), tick: hold(1.5), max: 4 },
      {
        name: "crypt",
        start: () => {
          const room = w.rooms.get("crypt");
          if (room) at(room.place.x, room.place.z - 1, Math.PI)();
        },
        tick: hold(1.5),
        max: 4,
      },
    ];
    for (const sg of segs) {
      const st = sg.start;
      sg.start = () => {
        t0.s = d.runtime.clock.time;
        st?.();
      };
    }
    runTour({
      kind: "places",
      renderer,
      scene,
      composer: look.composer,
      game: { simT: () => d.runtime.clock.time, pause: (on) => void (d.runtime.paused = on), advanceTime: (ms, draw) => void d.runtime.advance(ms, { draw: draw ?? true }) },
      segments: segs,
      ffMs,
      route: "places",
      estSeconds: 14,
      tier: d.tier,
      ui,
      readyAt: performance.now(),
      quality: () => ({ ...look.report() }),
      extra: () => ({ places: { pieces: w.placed.length, interiors: w.interiors.length, doors: w.doors.length, water: w.water.length } }),
      onEnd: () => {
        stick(0, 0);
        rig().yawHeld = false;
      },
    });
  };
  return {
    /** The interiors director's state, the view's (cut-away, light, fade), and the last events. */
    state: () => ({ ...d.sim().state(), view: d.view()?.state() ?? null, log: d.sim().log.slice(-20) }),
    /** Every interior (seamless or transition) with its room's box, and every transition door. */
    interiors: () => ({ rooms: w.interiors.map((i) => ({ ...i, bounds: { ...i.bounds } })), doors: w.doors.map((x) => ({ ...x })) }),
    /** Warp the hero `dist` m outside a room's door facing it (the sim's camera yaw held). Returns the yaw and the door. */
    approach,
    /** Let the camera's yaw follow the rig again. */
    release: () => void (rig().yawHeld = false),
    /** The water surface over (x, z) as the game's collision world answers it (the controller's own query), or null. */
    waterAt: (x: number, z: number): number | null => {
      rig().collision.faults.waterBoxOnly = PLACES_FAULTS["places-water-box"];
      return rig().collision.waterSurface(x, z);
    },
    /** The water at a point (its volume, surface, depth), or null. */
    waterProbe: (x: number, y: number, z: number) => rig().collision.waterAt({ x, y, z }),
    /** The ground's height (no water) at (x, z). */
    ground: (x: number, z: number) => w.ground(x, z),
    /** The water bodies: id, kind of shape, box. */
    water: () => w.water.map((v) => ({ id: v.id, shape: v.shape?.type ?? "box", min: { ...v.min }, max: { ...v.max }, surface: v.surface ?? v.max.y })),
    /** The placed pieces. */
    pieces: () => w.placed.map((p) => ({ id: p.id, piece: p.piece.name, group: p.group, owner: p.owner, ...p.place, triangles: p.piece.triangles, colliders: p.piece.colliders.length, room: !!p.piece.interior })),
    /** A hash over every placed piece's mesh (the same seeds, the same town). */
    kitHash: () => {
      let h = "";
      for (const p of w.placed) h += hashGeometry(p.piece.geometry);
      let x = 0x811c9dc5;
      for (let i = 0; i < h.length; i++) x = Math.imul(x ^ h.charCodeAt(i), 0x01000193);
      return (x >>> 0).toString(16).padStart(8, "0");
    },
    /** The view's meshes and their triangles. */
    meshes: () => {
      const v = d.view();
      if (!v) return null;
      const tri = (m: THREE.Mesh) => m.geometry.getAttribute("position").count / 3;
      const lod = v.meshes.town.levels.map((l) => ({ distance: l.distance, triangles: tri(l.object as THREE.Mesh) }));
      return { ground: tri(v.meshes.ground), town: lod, remote: tri(v.meshes.remote), water: tri(v.meshes.water) };
    },
    /** Draw one frame and read renderer.info: draw calls, triangles, programs, geometries. */
    counts: () => {
      const renderer = d.renderer();
      const look = d.look();
      if (!renderer || !look) return null;
      look.render();
      return { calls: renderer.info.render.calls, triangles: renderer.info.render.triangles, programs: renderer.info.programs?.length ?? 0, geometries: renderer.info.memory.geometries };
    },
    /** Gate C4's perf tour (fast-forward `ffMs` game ms per drawn frame); read `perf()`. */
    tour,
    perf: () => perfReport(),
  };
}
